// M3 achievements (Postwar & Offshore). check(state) is pure and cheap; false on an empty/bare state.
// State the sim must maintain for these (docs/M3_SPEC.md §16):
//   well.waterDepthFt (plot water depth at spud), well.offshore, well.platformUid, well.strikeDay, well.prod
//   well.roundWon (lease-round id the plot was won in), well.status / payFt (dry)
//   company.stats.futuresContracts, stormsRiddenOut, wellFiresCapped, eorProjects, peakNetWorth2025, concessionTake
//   rig.safety.daysWithoutIncident on offshore rigs, state.hedging.positions, state.fields[fid].storage.bbl
import { allWells } from '../achWells.js';
import { dateToDay } from '../../core/time.js';

const wells = allWells; // shared per tick (achWells.js)
const producing = (w) => !!w.prod || w.status === 'PRODUCING' || w.status === 'COMPLETING';
const isDry = (w) => w.status === 'DRY' || (w.status === 'PLUGGED' && !w.prod && !(w.payFt > 0));
const stat = (s, k) => s?.company?.stats?.[k] ?? 0;
const day = (s) => s?.day ?? -Infinity;

const D1946 = dateToDay(1946, 1, 1);
const D_EMBARGO = dateToDay(1973, 10, 16), D_EMBARGO_END = dateToDay(1974, 3, 18);
const D1973 = dateToDay(1973, 10, 16);
const D1987 = dateToDay(1987, 1, 1), D1988 = dateToDay(1988, 1, 1);
const D1999 = dateToDay(1999, 1, 1), D2000 = dateToDay(2000, 1, 1);
const D_TAPS = dateToDay(1977, 6, 20);
const D_SAFETY_CASE = dateToDay(1993, 5, 31);

const RAW = [
  { id: 'out_of_sight', name: 'Out of Sight of Land', icon: 'wave', era: 'postwar',
    desc: 'Complete a producing well offshore, like Kerr-McGee at Ship Shoal in 1947.',
    check: (s) => wells(s).some((w) => producing(w) && (w.offshore || (w.waterDepthFt ?? 0) > 0)) },
  { id: 'thousand_feet_of_water', name: 'Blue Water', icon: 'wave', era: 'offshore',
    desc: 'Spud a well in a thousand feet of water or more.',
    check: (s) => wells(s).some((w) => (w.waterDepthFt ?? 0) >= 1000) },
  { id: 'ain_dar', name: 'Ain Dar No. 1', icon: 'derrick', era: 'postwar',
    desc: 'Produce oil from Ghawar, the largest oil field on Earth.',
    check: (s) => wells(s).some((w) => w.fieldId === 'ghawar' && producing(w)) },
  { id: 'fifty_fifty', name: 'Fifty-Fifty', icon: 'coin', era: 'postwar',
    desc: 'Pay a host government $10 million in concession taxes. They are counting every barrel now.',
    check: (s) => stat(s, 'concessionTake') >= 10e6 },
  { id: 'seven_sisters', name: 'The Eighth Sister', icon: 'crown', era: 'postwar',
    desc: 'Reach a net worth of $5 billion (2025 dollars) before the 1973 embargo.',
    check: (s) => day(s) < D1973 && stat(s, 'peakNetWorth2025') >= 5e9 },
  { id: 'embargo_hoard', name: 'Barrels in the Bank', icon: 'barrel', era: 'offshore',
    desc: 'Hold a million barrels in storage during the Arab oil embargo.',
    check: (s) => day(s) >= D_EMBARGO && day(s) <= D_EMBARGO_END
      && Object.values(s?.fields ?? {}).reduce((a, f) => a + (f?.storage?.bbl ?? 0), 0) >= 1e6 },
  { id: 'paper_barrels', name: 'Paper Barrels', icon: 'chart', era: 'offshore',
    desc: 'Trade a NYMEX crude oil futures contract.',
    check: (s) => stat(s, 'futuresContracts') >= 1 || (s?.hedging?.positions?.length ?? 0) > 0 },
  { id: 'winners_curse', name: 'The Winner’s Curse', icon: 'dust', era: 'postwar',
    desc: 'Win a tract at a sealed-bid lease sale and drill it dry.',
    check: (s) => wells(s).some((w) => w.roundWon && isDry(w)) },
  { id: 'north_to_valdez', name: 'North to Valdez', icon: 'pipe', era: 'offshore',
    desc: 'Ship Prudhoe Bay crude through the Trans-Alaska Pipeline.',
    check: (s) => day(s) >= D_TAPS && wells(s).some((w) => w.fieldId === 'prudhoe_bay' && producing(w) && (w.prod?.cum ?? 0) > 0) },
  { id: 'storm_rider', name: 'Storm Rider', icon: 'storm', era: 'postwar',
    desc: 'Keep an offshore rig on location through a storm without losing a man or a leg.',
    check: (s) => stat(s, 'stormsRiddenOut') >= 1 },
  { id: 'hellfighter', name: 'Hellfighter', icon: 'flame', era: 'offshore',
    desc: 'Cap a burning well in Kuwait in 1991.',
    check: (s) => stat(s, 'wellFiresCapped') >= 1 },
  { id: 'tertiary', name: 'Tertiary Recovery', icon: 'drop', era: 'postwar',
    desc: 'Run a CO₂ or steam flood: get the oil the waterflood left behind.',
    check: (s) => stat(s, 'eorProjects') >= 1 },
  { id: 'safety_case', name: 'Safety Case', icon: 'shield', era: 'offshore',
    desc: 'Run an offshore rig 1,000 days without an incident under the safety case regime.',
    check: (s) => day(s) >= D_SAFETY_CASE && Object.values(s?.rigs ?? {}).some((r) => r?.offshore && (r?.safety?.daysWithoutIncident ?? 0) >= 1000) },
  { id: 'netback', name: 'Ten-Dollar Oil', icon: 'barrel', era: 'offshore',
    desc: 'Come through the 1986 price war: be solvent in 1987 with a producing well.',
    check: (s) => day(s) >= D1987 && day(s) < D1988 && (s?.company?.cash ?? -1) > 0 && wells(s).some(producing) },
  { id: 'nine_dollar_brent', name: 'Nine-Dollar Brent', icon: 'barrel', era: 'offshore',
    desc: 'Still producing offshore in 1999, after Brent fell to nine dollars.',
    check: (s) => day(s) >= D1999 && day(s) < D2000 && (s?.company?.cash ?? -1) > 0 && wells(s).some((w) => producing(w) && (w.offshore || (w.waterDepthFt ?? 0) > 0)) },
];

// Every M3 achievement is locked before 1 Jan 1946, whatever the state says (M1/M2 invariance, tested).
export const M3_ACHIEVEMENTS = RAW.map((a) => ({ ...a, check: (s) => day(s) >= D1946 && !!a.check(s) }));
